import { useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";

const SectionTitle = styled.p`
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const InfoBox = styled.div`
  background: #f2f5ff;
  border-left: 4px solid #004aad;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 13px;
  color: #263b4b;
  line-height: 1.5;
  margin-top: 4px;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #94a3b8;
  padding: 0 12px;
  box-sizing: border-box;
  outline: none;
  font-size: 13px;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`;

const ListShell = styled.div`
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 360px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }
`;

const ListHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  color: #475569;
  z-index: 1;
`;

const EquipRow = styled.label`
  display: grid;
  grid-template-columns: 24px 70px 1fr 110px 110px 90px 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const EquipCell = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Responsavel = styled.span`
  color: #1e3a8a;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const Counter = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: #263b4b;
`;

export default function ModalGerarTermos({ onClose, equipamentos, onSaved }) {
  const api = useApi();

  const atribuidos = useMemo(
    () => (equipamentos ?? []).filter((e) => e.responsavel !== "—"),
    [equipamentos]
  );

  const [busca, setBusca] = useState("");
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const filtrados = useMemo(() => {
    const query = busca.trim().toUpperCase();
    if (!query) return atribuidos;
    return atribuidos.filter((e) => {
      const hay = [
        e.id,
        e.tipo,
        e.descricao,
        e.patrimonio,
        e.serial,
        e.responsavel,
        e.nome,
        e.funcao,
        e.centro_custo,
      ]
        .filter(Boolean)
        .join(" ")
        .toUpperCase();
      return hay.includes(query);
    });
  }, [atribuidos, busca]);

  const todosFiltradosSelecionados =
    filtrados.length > 0 && filtrados.every((e) => selecionados.has(e.id));

  const toggleEquip = (id) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTodos = () => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (todosFiltradosSelecionados) {
        filtrados.forEach((e) => next.delete(e.id));
      } else {
        filtrados.forEach((e) => next.add(e.id));
      }
      return next;
    });
  };

  const colaboradoresUnicos = useMemo(() => {
    const set = new Set();
    selecionados.forEach((id) => {
      const eq = atribuidos.find((e) => e.id === id);
      if (eq && eq.responsavel && eq.responsavel !== "—") {
        set.add(eq.responsavel);
      }
    });
    return set.size;
  }, [selecionados, atribuidos]);

  const canSubmit = selecionados.size > 0 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);
      const { data } = await api.post(
        "/Painel/API/InventarioTI/GerarTermos/",
        {
          ids_ativo: Array.from(selecionados).map((id) => Number(id)),
        }
      );
      console.log(data);
      if (data?.total_falhas && data.total_falhas > 0) {
        alert(
          data.message ||
            `Envio parcial: ${data.total_enviados ?? 0} sucesso(s), ${data.total_falhas} falha(s).`
        );
      }
      onClose?.();
      onSaved?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao gerar termos.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="lg">
        <Formulario onSubmit={handleSubmit}>
          <SectionTitle>EMISSÃO MANUAL DE TERMOS DE RESPONSABILIDADE</SectionTitle>
          <InfoBox>
            Selecione os equipamentos abaixo para gerar os Termos de
            Responsabilidade dos colaboradores vinculados. Será enviado{" "}
            <strong>um termo por colaborador</strong> ao e-mail{" "}
            <strong>murilo.melo@palmont.com.br</strong>, contendo a relação dos
            equipamentos selecionados de cada um.
          </InfoBox>

          <div>
            <SectionTitle>EQUIPAMENTOS ATRIBUÍDOS</SectionTitle>
            <SearchInput
              placeholder="Pesquisar por patrimônio, serial, equipamento, matrícula, nome..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />

            <ListShell>
              <ListHeader>
                <input
                  type="checkbox"
                  checked={todosFiltradosSelecionados}
                  onChange={toggleTodos}
                  disabled={filtrados.length === 0}
                  aria-label="Selecionar todos"
                />
                <span>Selecionar todos</span>
                <Counter>
                  {selecionados.size} selecionado
                  {selecionados.size === 1 ? "" : "s"}
                  {selecionados.size > 0 && colaboradoresUnicos > 0
                    ? ` • ${colaboradoresUnicos} colaborador${
                        colaboradoresUnicos === 1 ? "" : "es"
                      }`
                    : ""}{" "}
                  • {filtrados.length} exibido
                  {filtrados.length === 1 ? "" : "s"}
                </Counter>
              </ListHeader>

              {filtrados.length === 0 ? (
                <EmptyRow>
                  {atribuidos.length === 0
                    ? "Nenhum equipamento atribuído a colaboradores no momento."
                    : "Nenhum equipamento encontrado para a busca."}
                </EmptyRow>
              ) : (
                filtrados.map((e) => (
                  <EquipRow key={e.id}>
                    <input
                      type="checkbox"
                      checked={selecionados.has(e.id)}
                      onChange={() => toggleEquip(e.id)}
                    />
                    <EquipCell title={e.id}>{e.id}</EquipCell>
                    <EquipCell title={e.descricao}>
                      {e.tipo !== "—" ? `${e.tipo} — ` : ""}
                      {e.descricao}
                    </EquipCell>
                    <EquipCell title={e.patrimonio}>{e.patrimonio}</EquipCell>
                    <EquipCell title={e.serial}>{e.serial}</EquipCell>
                    <EquipCell title={e.responsavel}>{e.responsavel}</EquipCell>
                    <Responsavel title={e.nome}>
                      {e.nome && e.nome !== "—" ? e.nome : ""}
                    </Responsavel>
                  </EquipRow>
                ))
              )}
            </ListShell>
          </div>

          <FormComp
            type="submit"
            valor={
              saving
                ? "ENVIANDO..."
                : `ENVIAR TERMOS${
                    selecionados.size > 0 ? ` (${selecionados.size})` : ""
                  }`
            }
            disabled={!canSubmit}
          />
        </Formulario>
      </Modal>
    </Overlay>
  );
}
